/* eslint-disable no-nested-ternary */
import { useFloating } from '@floating-ui/react-native'
import { HAS_ISLAND, IS_WEB } from 'constants/platform'
import { useTheme } from 'contexts'
import React, { useCallback, useState } from 'react'
import {
  Pressable,
  Modal as ReactNativeModal,
  StyleSheet,
  View,
  useWindowDimensions,
} from 'react-native'

import { SearchBar } from 'pages/Chat/widgets/SearchBar'
import { SearchResults } from 'pages/Chat/widgets/SearchResults'

import { useMaxWidth } from 'hooks/useMaxWidth'
import { useUserSearch } from 'hooks/useUserSearch'

const Backdrop = ({ children, ...rest }) => {
  const dimensions = useWindowDimensions()
  const backdropStyles = StyleSheet.create({
    pressable: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: dimensions.width,
      height: dimensions.height,
      zIndex: 888,
      backgroundColor: 'rgba(0,0,0,.33)',
    },
  })
  return (
    <Pressable style={backdropStyles.pressable} {...rest}>
      {children}
    </Pressable>
  )
}

export const FloatingSearchBar = ({
  searchPrompt,
  width,
  shouldPadTop,
  isNav,
}: {
  searchPrompt: string
  width: number | string
  shouldPadTop?: boolean
  isNav?: boolean
}) => {
  const { height } = useMaxWidth()

  const [isSearching, setIsSearching] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const { data: matchingUsers = [] } = useUserSearch({ searchTerm })
  const [searchResultsWasClosed, setSearchResultsWasClosed] = useState(false)
  const showSearchResults =
    !searchResultsWasClosed && searchTerm.length > 2 && matchingUsers.length > 0

  const {
    x,
    y,
    reference: floatingRef,
    floating,
  } = useFloating({
    placement: 'bottom',
    middleware: [],
  })

  const searchTermHandler = useCallback(
    (value: string) => {
      setSearchTerm(value)
      setIsSearching(true)
      setSearchResultsWasClosed(false)
    },
    [setSearchTerm],
  )

  const isSearchingHandler = useCallback(
    (value: boolean) => {
      setIsSearching(value)
    },
    [setIsSearching],
  )

  const navigateToUserProfile = useCallback(() => {
    setIsSearching(false)
    setSearchTerm('')
  }, [])

  const handleModalClose = () => {
    setSearchResultsWasClosed(true)
    setSearchTerm('')
  }

  const theme = useTheme()
  const computedStyles = StyleSheet.create({
    floatingSearchResults: {
      top:
        y && !isNav
          ? y + (!IS_WEB ? (HAS_ISLAND ? 62 : height > 667 ? 50 : 24) : 48)
          : isNav
          ? 24 + (!IS_WEB ? (HAS_ISLAND ? 36 : height > 812 ? 24 : height > 667 ? 28 : -2) : 0)
          : 0,
      left:
        x && !isNav ? theme.spacing.md : isNav ? 64 + (!IS_WEB ? (height > 812 ? 12 : 2) : 0) : 0,
      width,
      zIndex: 999,
      borderRadius: 10,
      display: x && x > 0 && y && y > 0 ? undefined : 'none',
    },
    refView: {
      width: '100%',
    },
  })

  return (
    <>
      <View ref={floatingRef} style={computedStyles.refView}>
        <SearchBar
          shouldPadTop={shouldPadTop}
          autoFocus={isSearching && !showSearchResults}
          searchTerm={searchTerm}
          setSearchTerm={searchTermHandler}
          setIsSearching={isSearchingHandler}
          placeholder={searchPrompt}
          showConnectedSearchResults={showSearchResults}
        />
      </View>

      <ReactNativeModal
        animationType='none'
        onRequestClose={handleModalClose}
        visible={showSearchResults}
        transparent
      >
        <Backdrop
          onPress={(event) => event.target === event.currentTarget && handleModalClose()}
          pointerEvents='auto'
        >
          <View
            testID='FloatingSearchResults'
            ref={floating}
            style={computedStyles.floatingSearchResults}
          >
            <SearchBar
              shouldPadTop={false}
              autoFocus={isSearching && showSearchResults}
              searchTerm={searchTerm}
              setSearchTerm={searchTermHandler}
              setIsSearching={isSearchingHandler}
              placeholder={searchPrompt}
              showConnectedSearchResults={showSearchResults}
            />
            <SearchResults
              matchingUsers={matchingUsers}
              onUserPress={navigateToUserProfile}
              visible={showSearchResults}
              maxHeight={height * 0.6}
            />
          </View>
        </Backdrop>
      </ReactNativeModal>
    </>
  )
}
